import {
  Card,
  CircleLoader,
  ConfirmationModal,
  EmptyState,
  Header,
  IconButton,
  Space,
  Spacer,
  StatusBadge,
  WithTooltip,
} from '@procurify/ui'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { ErrorBoundary } from '@webapp/components/ErrorBoundary'
import { Sheet } from '@webapp/components/Sheet'
import { TabContentFooter } from '@webapp/components/TabContentFooter'
import { TabPanes } from '@webapp/components/TabPanes'
import { PageActions } from '@webapp/core/components/Page'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useProcurifyIntl } from '@webapp/hooks'
import { ExpenseViewerRoles } from '@webapp/modules/expense/components/ExpenseItemTable'
import { ExpenseStatusLabel } from '@webapp/modules/expense/components/ExpenseStatusLabel'
import { ExpenseApprovalHistoryPresenter } from '@webapp/modules/expense/features/get-expense-approval-history/presenters/ExpenseApprovalHistoryPresenter'
import { ExpenseCostSummaryPresenter } from '@webapp/modules/expense/features/get-expense-report-detail/presenters/ExpenseCostSummaryPresenter'
import { ExpensePresenter } from '@webapp/modules/expense/features/get-expense-report-detail/presenters/ExpensePresenter'
import { ApprovalReminderIconButton } from '@webapp/modules/expense/features/send-approval-reminder/presenters/ApprovalReminderIconButton'
import { SpendTrackerPresenter } from '@webapp/modules/expense/features/show-budget-info/presenters/SpendTrackerPresenter'
import {
  AuditLogTabContent,
  ExpenseCommentPopoverButton,
  HeaderActions,
} from './components'
import messages from './messages'
import { ExpenseDetailsViewStyled } from './styles/ExpenseDetailsViewStyled'
import { useExpenseView } from './useExpenseView'

export interface IExpenseViewProps {
  uuid: string
  onPermissionsLoaded?: (data: { canApprove: boolean }) => void
}

export const ExpenseView = ({
  uuid,
  onPermissionsLoaded,
}: IExpenseViewProps) => {
  const intl = useProcurifyIntl()
  const history = useHistory()
  const { enable_flexible_spend_tracking: isFlexibleSpendTrackingEnabled } =
    useSelector(makeSelectFeatureSwitches())

  const {
    data: { expense, isLoading },
    states: {
      showSpendTracker,
      isRequester,
      showSpendTrackerButton,
      showComments,
      expenseContentTypeId,
      userId,
      isApproving,
      isOverBudgetConfirmOpen,
      isDenying,
      isDeleting,
      isDeleteConfirmOpen,
      isDuplicating,
      activeTab,
      canApprove,
      canDelete,
      showEditButton,
      showReviseButton,
      isPending,
      hasNextApproverChoices,
      isFinalApproval,
      nextApprover,
      showSendReminder,
      showHistory,
      showGeneratePdf,
      selectedNextApproverOption,
    },
    commands: {
      approveExpense,
      confirmOverBudget,
      cancelOverBudget,
      denyExpense,
      deleteExpenseReport,
      openDeleteConfirm,
      closeDeleteConfirm,
      duplicateExpense,
      openPdf,
    },
    methods: {
      onSelectedNextApproverIdChange,
      onOpenSpendTracker,
      onCloseSpendTracker,
      changeActiveTab,
    },
  } = useExpenseView({ uuid, onPermissionsLoaded })

  const showApprovalSection = canApprove && isPending
  const showHeaderActions =
    showEditButton || showReviseButton || showApprovalSection

  if (isLoading) {
    return <CircleLoader />
  }

  return expense ? (
    <>
      <PageActions>
        <Space direction='horizontal'>
          {canDelete && (
            <WithTooltip
              title={intl.formatMessage(messages.deleteActionButton)}
            >
              <IconButton
                icon='delete'
                aria-label={intl.formatMessage(messages.deleteActionButton)}
                onClick={openDeleteConfirm}
                disabled={isDeleting}
              />
            </WithTooltip>
          )}

          {isRequester && (
            <WithTooltip
              title={intl.formatMessage(messages.duplicateToolbarButton)}
            >
              <IconButton
                icon='duplicate'
                aria-label={intl.formatMessage(messages.duplicateToolbarButton)}
                onClick={duplicateExpense}
                disabled={isDuplicating}
              />
            </WithTooltip>
          )}

          {showHistory && expense && (
            <ExpenseApprovalHistoryPresenter logs={expense.logs} />
          )}

          {showComments && expense && (
            <ExpenseCommentPopoverButton
              contentTypeId={expenseContentTypeId}
              objectId={expense.id}
              userId={userId}
            />
          )}

          {showGeneratePdf && expense && (
            <WithTooltip
              title={intl.formatMessage(messages.generatePdfToolbarButton)}
            >
              <IconButton
                icon='print'
                aria-label={intl.formatMessage(
                  messages.generatePdfToolbarButton
                )}
                onClick={openPdf}
              />
            </WithTooltip>
          )}

          {showSendReminder && expense && nextApprover && (
            <ApprovalReminderIconButton
              expenseId={expense.id}
              recipient={nextApprover}
            />
          )}

          {showSpendTrackerButton && (
            <WithTooltip
              title={intl.formatMessage(
                isFlexibleSpendTrackingEnabled
                  ? messages.budgetsToolbarButton
                  : messages.spendTrackerToolbarButton
              )}
            >
              <IconButton
                icon='report'
                aria-label={intl.formatMessage(
                  isFlexibleSpendTrackingEnabled
                    ? messages.budgetsToolbarButton
                    : messages.spendTrackerToolbarButton
                )}
                onClick={onOpenSpendTracker}
              />
            </WithTooltip>
          )}
        </Space>
      </PageActions>

      <Card>
        <Card.Content>
          <ExpenseDetailsViewStyled>
            {expense && (
              <Space
                direction='horizontal'
                justify='space-between'
                align='start'
              >
                <Space>
                  <Space direction='horizontal' align='center' flexWrap>
                    <Header as='h5'>
                      {intl.formatMessage(messages.reportHeaderTitle, {
                        id: expense.id,
                      })}
                    </Header>

                    {isFinalApproval ? (
                      <StatusBadge
                        aria-label={intl.formatMessage(
                          messages.pendingFinalApprovalStatusText
                        )}
                        size={'small'}
                        type={'warning'}
                        text={intl.formatMessage(
                          messages.pendingFinalApprovalStatusText
                        )}
                      />
                    ) : (
                      <ExpenseStatusLabel
                        size='medium'
                        status={expense.status}
                      />
                    )}
                  </Space>

                  <Header as='h2'>{expense.title}</Header>
                </Space>

                <Space direction='horizontal' justify='end'>
                  {showHeaderActions && (
                    <HeaderActions
                      showEdit={showEditButton}
                      showRevise={showReviseButton}
                      showApprovalActions={showApprovalSection}
                      nextApproverOptions={expense.nextApproverChoices}
                      selectedNextApproverOption={selectedNextApproverOption}
                      onSelectedNextApproverIdChange={
                        onSelectedNextApproverIdChange
                      }
                      hasNextApproverChoices={hasNextApproverChoices}
                      isFinalApproval={isFinalApproval}
                      isApproving={isApproving}
                      isDenying={isDenying}
                      onEdit={() => history.push(`/expenses/${uuid}/edit`)}
                      onRevise={() => history.push(`/expenses/${uuid}/revise`)}
                      onApprove={approveExpense}
                      onDeny={denyExpense}
                    />
                  )}
                </Space>
              </Space>
            )}

            <TabPanes activeKey={activeTab} onChange={changeActiveTab}>
              <TabPanes.Pane
                label={intl.formatMessage(messages.detailsTabLabel)}
                key='detail'
              >
                <ExpensePresenter
                  uuid={uuid}
                  showCostSummary={false}
                  showHeader={false}
                  viewerRole={
                    isRequester
                      ? ExpenseViewerRoles.REQUESTER
                      : ExpenseViewerRoles.APPROVER
                  }
                />

                <TabContentFooter>
                  <Space direction='horizontal' justify='end'>
                    <ExpenseCostSummaryPresenter uuid={uuid} />
                  </Space>
                </TabContentFooter>
              </TabPanes.Pane>

              <TabPanes.Pane
                label={intl.formatMessage(messages.auditLogTabLabel)}
                key='audit-log'
              >
                {expense && (
                  <AuditLogTabContent
                    expenseContentTypeId={expenseContentTypeId}
                    expenseId={String(expense.id)}
                  />
                )}
              </TabPanes.Pane>
            </TabPanes>
          </ExpenseDetailsViewStyled>
        </Card.Content>
      </Card>

      <Sheet isOpen={showSpendTracker}>
        <Sheet.Header
          onDismiss={onCloseSpendTracker}
          iconButtonLabel={intl.formatMessage(
            isFlexibleSpendTrackingEnabled
              ? messages.hideBudgetTrackerButtonLabel
              : messages.hideSpendTrackerButtonLabel
          )}
        >
          {intl.formatMessage(
            isFlexibleSpendTrackingEnabled
              ? messages.budgetsToolbarButton
              : messages.spendTrackerToolbarButton
          )}
        </Sheet.Header>
        <Sheet.Content>
          <ErrorBoundary boundaryName='SpendTrackerSheet'>
            <SpendTrackerPresenter expenseUuid={uuid} />
          </ErrorBoundary>
        </Sheet.Content>
      </Sheet>

      <ConfirmationModal
        open={isDeleteConfirmOpen}
        type='danger'
        header={intl.formatMessage(messages.deleteConfirmHeader)}
        content={intl.formatMessage(messages.deleteConfirmMessage)}
        onConfirm={async (done) => {
          await deleteExpenseReport()
          done()
        }}
        handleDismiss={closeDeleteConfirm}
      />

      <ConfirmationModal
        open={isOverBudgetConfirmOpen}
        type='warning'
        header={intl.formatMessage(messages.overBudgetConfirmHeader)}
        content={intl.formatMessage(messages.overBudgetConfirmMessage)}
        onConfirm={async (done) => {
          await confirmOverBudget()
          done()
        }}
        handleDismiss={cancelOverBudget}
      />
    </>
  ) : (
    <>
      <Spacer space={6} />
      <EmptyState
        context='404'
        content={intl.formatMessage(messages.expenseNotFoundTitle)}
      />
      <Spacer space={6} />
    </>
  )
}

ExpenseView.displayName = 'ExpenseView'
